import { cn } from '@/lib/utils';
import { Tag } from 'antd';

// ── Status color mapping (Tailwind chip) ─────────────────────────────────────

const STATUS_CLASSES: Record<string, string> = {
  published:  'bg-coral text-white',
  completed:  'bg-teal text-white',
  draft:      'bg-teal text-white',
  generating: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  pending:    'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
  failed:     'bg-destructive/10 text-destructive',
};

const DEFAULT_CLASS = 'bg-muted text-muted-foreground';

const SIZE_CLASSES = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
} as const;

// ── StatusBadge (Tailwind chip for cards/calendar cells) ─────────────────────

interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function StatusBadge({ status, size = 'sm', className }: StatusBadgeProps) {
  const normalized = status.toLowerCase();
  const colorClass = STATUS_CLASSES[normalized] ?? DEFAULT_CLASS;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium capitalize leading-none',
        colorClass,
        SIZE_CLASSES[size],
        className,
      )}
    >
      {normalized}
    </span>
  );
}

// ── StatusTag (antd Tag for table/drawer contexts) ───────────────────────────

type TagStyle = { color?: string; style?: React.CSSProperties };

function getTagStyle(status: string): TagStyle {
  switch (status.toLowerCase()) {
    case 'published':
      return {
        style: {
          backgroundColor: 'var(--color-coral)',
          color: '#fff',
          borderColor: 'var(--color-coral)',
        },
      };
    case 'completed':
    case 'draft':
      return {
        style: {
          backgroundColor: 'var(--color-teal)',
          color: '#fff',
          borderColor: 'var(--color-teal)',
        },
      };
    case 'generating':
    case 'pending':
      return { color: 'orange' };
    case 'failed':
      return { color: 'red' };
    default:
      return {};
  }
}

interface StatusTagProps {
  status: string;
  className?: string;
}

export function StatusTag({ status, className }: StatusTagProps) {
  const normalized = status.toLowerCase();
  const tagStyle = getTagStyle(normalized);

  return (
    <Tag className={className} {...tagStyle}>
      {normalized.charAt(0).toUpperCase() + normalized.slice(1)}
    </Tag>
  );
}
